import {
  createShiftPatternSchema,
  createVisitSchema,
  type BoardCaregiver,
  type ClientDetail,
  type ClientList,
  type ConflictCheck,
  type ConflictFindingView,
  type ShiftPatternWriteResult,
} from '@homecare/contracts';
import { addDays, dayOfWeek, instantOf, toLocalDate } from '@homecare/domain';
import { Button, Field, Modal, Notice, SelectField } from '@homecare/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { useUnsavedChangesGuard } from '../../lib/unsaved-changes';

/**
 * Book a visit — or a weekly pattern of them — through every rule.
 *
 * ## Choices, not identifiers
 *
 * RECOVERY §3: book-a-visit once required raw UUIDs. Every field here is a
 * name somebody recognises: the client by name, the address by its street,
 * the service by its code, the caregiver by name.
 *
 * ## Times are entered in the visit's zone and sent as instants
 *
 * The date and the two clock times are the service location's wall clock —
 * the address's zone, or the agency's when the address has none, which is
 * what the server falls back to as well. They are turned into instants with
 * the location's offset here, by `instantOf`, and the server works the local
 * fields back out of those instants in the same zone. Nothing crosses the wire
 * as a naked local datetime, and nothing is read back in the browser's zone.
 * An end time at or before the start is the next morning: an overnight.
 *
 * ## The engine is asked first
 *
 * "Check the rules" calls POST /v1/visits/check and shows all fifteen answers
 * that matter: what refuses, what this person may override with a reason, and
 * what only warns. Booking sends the same body with those reasons.
 */

interface Draft {
  clientId: string;
  clientAddressId: string;
  serviceCodeId: string;
  caregiverId: string;
  date: string;
  startTime: string;
  endTime: string;
  repeat: boolean;
  days: number[];
  until: string;
}

interface ServiceCodeOption {
  id: string;
  code: string;
  description: string | null;
  isActive: boolean;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const minuteOf = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
};

export function BookVisit({
  open,
  onClose,
  defaultDate,
  agencyTimeZone,
  caregivers,
}: {
  open: boolean;
  onClose: () => void;
  defaultDate: string;
  agencyTimeZone: string;
  caregivers: readonly BoardCaregiver[];
}): ReactNode {
  return (
    <Modal
      open={open}
      onOpenChange={(next) => (next ? undefined : onClose())}
      title="Book a visit"
      description="Choose who, where and when. The rules are checked before anything is saved."
      className="max-h-[calc(100vh-4rem)] w-[min(44rem,calc(100vw-2rem))] overflow-y-auto"
    >
      {open && (
        <BookingForm
          defaultDate={defaultDate}
          agencyTimeZone={agencyTimeZone}
          caregivers={caregivers}
          onDone={onClose}
        />
      )}
    </Modal>
  );
}

function BookingForm({
  defaultDate,
  agencyTimeZone,
  caregivers,
  onDone,
}: {
  defaultDate: string;
  agencyTimeZone: string;
  caregivers: readonly BoardCaregiver[];
  onDone: () => void;
}): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();

  const pristine: Draft = {
    clientId: '',
    clientAddressId: '',
    serviceCodeId: '',
    caregiverId: '',
    date: defaultDate,
    startTime: '09:00',
    endTime: '13:00',
    repeat: false,
    days: [dayOfWeek(toLocalDate(defaultDate))],
    until: '',
  };
  const [draft, setDraftState] = useState<Draft>(pristine);
  const [search, setSearch] = useState('');
  const [check, setCheck] = useState<ConflictCheck | null>(null);
  const [reasons, setReasons] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<'check' | 'book' | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [patternResult, setPatternResult] = useState<ShiftPatternWriteResult | null>(null);

  const dirty = JSON.stringify(draft) !== JSON.stringify(pristine);
  useUnsavedChangesGuard(dirty && patternResult === null);

  /** Any change invalidates the engine's last answer — it was about a different visit. */
  const setDraft = (change: Partial<Draft>): void => {
    setDraftState((was) => ({ ...was, ...change }));
    setCheck(null);
    setFailure(null);
  };

  const clients = useQuery({
    queryKey: ['clients', 'booking-search', search],
    queryFn: () =>
      api.get<ClientList>(
        `/v1/clients?pageSize=25${search.trim() ? `&search=${encodeURIComponent(search.trim())}` : ''}`,
      ),
  });
  const client = useQuery({
    queryKey: ['clients', draft.clientId],
    queryFn: () => api.get<ClientDetail>(`/v1/clients/${draft.clientId}`),
    enabled: draft.clientId !== '',
  });
  const serviceCodes = useQuery({
    queryKey: ['payers', 'service-codes'],
    queryFn: () => api.get<{ serviceCodes: ServiceCodeOption[] }>('/v1/service-codes'),
    staleTime: 5 * 60 * 1000,
  });

  const address = client.data?.addresses.find((entry) => entry.id === draft.clientAddressId);
  const zone = address?.timezone ?? agencyTimeZone;

  const span = useMemo(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.date) || !draft.startTime || !draft.endTime) return null;
    const day = toLocalDate(draft.date);
    const start = minuteOf(draft.startTime);
    const end = minuteOf(draft.endTime);
    const overnight = end <= start;
    return {
      startsAt: instantOf(day, start, zone).toISOString(),
      endsAt: instantOf(overnight ? addDays(day, 1) : day, end, zone).toISOString(),
      overnight,
    };
  }, [draft.date, draft.startTime, draft.endTime, zone]);

  const overrides = (check?.overridable ?? [])
    .filter((finding) => (reasons[finding.rule] ?? '').trim())
    .map((finding) => ({ rule: finding.rule, reason: (reasons[finding.rule] ?? '').trim() }));

  const body = {
    clientId: draft.clientId,
    clientAddressId: draft.clientAddressId,
    serviceCodeId: draft.serviceCodeId,
    caregiverId: draft.caregiverId || null,
    startsAt: span?.startsAt ?? '',
    endsAt: span?.endsAt ?? '',
    overrides,
  };

  /** The shared contract decides what is wrong with the form, inline, before the API does. */
  const validate = (): boolean => {
    const parsed = createVisitSchema.safeParse(body);
    const found: Record<string, string> = {};
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form');
        found[key] ??= issue.message;
      }
    }
    if (!draft.clientId) found['clientId'] = 'Choose the client.';
    if (!draft.clientAddressId) found['clientAddressId'] = 'Choose where the visit is.';
    if (!draft.serviceCodeId) found['serviceCodeId'] = 'Choose the service.';
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const runCheck = async (): Promise<void> => {
    if (!validate()) return;
    setBusy('check');
    setFailure(null);
    try {
      setCheck(await api.send<ConflictCheck>('POST', '/v1/visits/check', body));
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(null);
    }
  };

  const book = async (): Promise<void> => {
    if (!validate()) return;
    setBusy('book');
    setFailure(null);
    try {
      if (draft.repeat) {
        const pattern = {
          clientId: draft.clientId,
          clientAddressId: draft.clientAddressId,
          serviceCodeId: draft.serviceCodeId,
          caregiverId: draft.caregiverId || null,
          daysOfWeek: draft.days,
          startTime: draft.startTime,
          endTime: draft.endTime,
          weekInterval: 1,
          effectiveFrom: draft.date,
          effectiveTo: draft.until || null,
        };
        const parsed = createShiftPatternSchema.safeParse(pattern);
        if (!parsed.success) {
          setErrors({ days: parsed.error.issues[0]?.message ?? 'Check the pattern.' });
          return;
        }
        setPatternResult(
          await api.send<ShiftPatternWriteResult>('POST', '/v1/shift-patterns', pattern),
        );
      } else {
        await api.send('POST', '/v1/visits', body);
      }
      await queryClient.invalidateQueries({ queryKey: ['schedule'] });
      if (!draft.repeat) onDone();
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(null);
    }
  };

  if (patternResult) {
    const { materialisation } = patternResult;
    return (
      <div className="grid gap-4">
        <Notice
          tone={materialisation.atRisk.length > 0 ? 'warning' : 'verified'}
          title={`The pattern is saved, and ${materialisation.booked} visits are on the board.`}
        >
          {materialisation.atRisk.length > 0 ? (
            <ul className="grid gap-1">
              {materialisation.atRisk.map((entry) => (
                <li key={entry.visitId}>
                  {entry.date}: {entry.reasons.map((reason) => reason.message).join(' ')}
                </li>
              ))}
            </ul>
          ) : (
            `Booked through ${materialisation.through ?? 'the next eight weeks'}. Every visit passed the rules.`
          )}
        </Notice>
        <div className="flex justify-end">
          <Button onClick={onDone}>Back to the board</Button>
        </div>
      </div>
    );
  }

  const blocked = check ? check.refusals.length > 0 : false;
  const unexplained = (check?.overridable ?? []).some((f) => !(reasons[f.rule] ?? '').trim());
  // A pattern is materialised and checked visit by visit server-side; at-risk
  // visits are flagged rather than refused, so it does not wait on a check.
  const canBook = draft.repeat || (check !== null && !blocked && !unexplained);

  return (
    <div className="grid gap-4" data-testid="booking-form">
      <ErrorNote error={failure} nextStep="Change what the reason names, then check again." />

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Find a client"
          hint="A name or an MRN."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <SelectField
          label="Client"
          value={draft.clientId}
          error={errors['clientId']}
          onChange={(event) => setDraft({ clientId: event.target.value, clientAddressId: '' })}
        >
          <option value="">{clients.isPending ? 'Loading clients…' : 'Choose a client'}</option>
          {(clients.data?.clients ?? []).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.preferredName ?? entry.firstName} {entry.lastName} · {entry.mrn}
            </option>
          ))}
        </SelectField>
      </div>

      <SelectField
        label="Where"
        value={draft.clientAddressId}
        error={errors['clientAddressId']}
        hint={
          draft.clientId && client.data?.addresses.length === 0
            ? 'This client has no address yet. Add one on their record first.'
            : undefined
        }
        onChange={(event) => setDraft({ clientAddressId: event.target.value })}
      >
        <option value="">{client.isFetching ? 'Loading addresses…' : 'Choose an address'}</option>
        {(client.data?.addresses ?? []).map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entry.line1}, {entry.city} {entry.state}
            {entry.isPrimary ? ' (primary)' : ''}
          </option>
        ))}
      </SelectField>

      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField
          label="Service"
          value={draft.serviceCodeId}
          error={errors['serviceCodeId']}
          onChange={(event) => setDraft({ serviceCodeId: event.target.value })}
        >
          <option value="">Choose a service</option>
          {(serviceCodes.data?.serviceCodes ?? [])
            .filter((code) => code.isActive)
            .map((code) => (
              <option key={code.id} value={code.id}>
                {code.code}
                {code.description ? ` — ${code.description}` : ''}
              </option>
            ))}
        </SelectField>
        <SelectField
          label="Caregiver"
          value={draft.caregiverId}
          hint="Leave it open to fill later from the board."
          onChange={(event) => setDraft({ caregiverId: event.target.value })}
        >
          <option value="">Leave it open</option>
          {caregivers.map((caregiver) => (
            <option key={caregiver.id} value={caregiver.id}>
              {caregiver.name}
            </option>
          ))}
        </SelectField>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Field
          label={draft.repeat ? 'First day' : 'Day'}
          type="date"
          value={draft.date}
          error={errors['startsAt']}
          onChange={(event) => setDraft({ date: event.target.value })}
        />
        <Field
          label="Starts"
          type="time"
          value={draft.startTime}
          onChange={(event) => setDraft({ startTime: event.target.value })}
        />
        <Field
          label="Ends"
          type="time"
          value={draft.endTime}
          error={errors['endsAt']}
          hint={span?.overnight ? 'Ends the next morning — an overnight.' : undefined}
          onChange={(event) => setDraft({ endTime: event.target.value })}
        />
      </div>
      <p className="text-ink-3 text-xs">Times are local to the visit ({zone}).</p>

      <div className="grid gap-2">
        <label className="text-ink flex items-center gap-2 text-sm font-semibold">
          <input
            type="checkbox"
            checked={draft.repeat}
            onChange={(event) => setDraft({ repeat: event.target.checked })}
          />
          Repeat every week
        </label>
        {draft.repeat && (
          <>
            <div className="flex flex-wrap gap-3" role="group" aria-label="Days it repeats on">
              {DAY_NAMES.map((name, day) => (
                <label key={name} className="text-ink flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.days.includes(day)}
                    onChange={(event) =>
                      setDraft({
                        days: event.target.checked
                          ? [...draft.days, day].sort()
                          : draft.days.filter((d) => d !== day),
                      })
                    }
                  />
                  {name.slice(0, 3)}
                </label>
              ))}
            </div>
            {errors['days'] && <p className="text-critical text-xs">{errors['days']}</p>}
            <Field
              label="Last day (optional)"
              type="date"
              hint="Leave it empty to keep it going; eight weeks are booked ahead at a time."
              value={draft.until}
              onChange={(event) => setDraft({ until: event.target.value })}
            />
          </>
        )}
      </div>

      {check && <Findings check={check} reasons={reasons} onReason={setReasons} />}

      <div className="flex flex-wrap justify-end gap-2">
        {!draft.repeat && (
          <Button variant="secondary" pending={busy === 'check'} onClick={() => void runCheck()}>
            Check the rules
          </Button>
        )}
        {canBook ? (
          <Button pending={busy === 'book'} onClick={() => void book()}>
            {draft.repeat ? 'Save the pattern' : 'Book the visit'}
          </Button>
        ) : (
          <Button
            disabled
            disabledReason={
              check === null
                ? 'Check the rules first.'
                : blocked
                  ? 'A rule refuses this visit. Change what it names.'
                  : 'Give a reason for each rule you are overriding.'
            }
          >
            Book the visit
          </Button>
        )}
      </div>
    </div>
  );
}

function Findings({
  check,
  reasons,
  onReason,
}: {
  check: ConflictCheck;
  reasons: Record<number, string>;
  onReason: (next: Record<number, string>) => void;
}): ReactNode {
  const clean =
    check.refusals.length === 0 && check.overridable.length === 0 && check.warnings.length === 0;

  return (
    <div className="grid gap-3" data-testid="findings" aria-live="polite">
      {clean && (
        <Notice tone="verified" title="Every rule passes.">
          Nothing stops this visit. Book it when you are ready.
        </Notice>
      )}
      {check.refusals.length > 0 && (
        <Notice tone="critical" title="This visit cannot be booked as it is">
          <FindingList findings={check.refusals} />
        </Notice>
      )}
      {check.overridable.length > 0 && (
        <Notice
          tone="warning"
          title="You may book past these — say why, and it goes on the override report"
        >
          <div className="grid gap-3">
            {check.overridable.map((finding) => (
              <Field
                key={finding.rule}
                label={`Rule ${finding.rule}: ${finding.message}`}
                value={reasons[finding.rule] ?? ''}
                onChange={(event) => onReason({ ...reasons, [finding.rule]: event.target.value })}
              />
            ))}
          </div>
        </Notice>
      )}
      {check.warnings.length > 0 && (
        <Notice tone="scheduled" title="Worth knowing — these do not stop the booking">
          <FindingList findings={check.warnings} />
        </Notice>
      )}
    </div>
  );
}

function FindingList({ findings }: { findings: readonly ConflictFindingView[] }): ReactNode {
  return (
    <ul className="grid gap-1">
      {findings.map((finding) => (
        <li key={finding.rule}>
          <span className="font-semibold">Rule {finding.rule}.</span> {finding.message}
        </li>
      ))}
    </ul>
  );
}
